import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTimePeriodContext } from "../contexts/TimePeriodContext";
import { ProjectCheck } from "../types/ProjectCheck";
import { GetProjectChecksAsync, SaveProjectCheckChangesAsync } from "../services/ProjectsService";

export function useProjectChecks() {
  const { instance, account, user } = useAuth();
  const { selectedPeriod, setDirty } = useTimePeriodContext();

  const [projectChecks, setProjectChecks] = useState<ProjectCheck[]>([]);
  const [originalProjectChecks, setOriginalProjectChecks] = useState<ProjectCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [hasCompletedCheck, setHasCompletedCheck] = useState(false);

  const allChecked = useMemo(
    () =>
      projectChecks.length > 0 &&
      projectChecks.every((c) => c.checkedByManager || c.managerComment.trim() !== ""),
    [projectChecks],
  );

  const hasChanges = useMemo(() => {
    return projectChecks.some((current, i) => {
      const original = originalProjectChecks[i];
      return (
        current.checkedByManager !== original?.checkedByManager ||
        current.managerComment !== original?.managerComment
      );
    });
  }, [projectChecks, originalProjectChecks]);

  const fetchProjectChecks = useCallback(async () => {
    setLoading(true);
    setFetchError(false);

    if (!user?.id) return;

    try {
      const response = await GetProjectChecksAsync(instance, account, user.id, selectedPeriod);

      if (!response) throw new Error();

      setHasCompletedCheck(response.hasCompleted);

      setProjectChecks(response.checks);
      setOriginalProjectChecks(response.checks);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id, instance, account, selectedPeriod]);

  useEffect(() => {
    fetchProjectChecks();
  }, [fetchProjectChecks]);

  const saveProjectChecks = useCallback(
    async (isCompleting: boolean) => {
      setLoading(true);
      setSaveError(false);
      try {
        await SaveProjectCheckChangesAsync(
          instance,
          account,
          projectChecks,
          isCompleting,
          selectedPeriod,
        );

        setOriginalProjectChecks([...projectChecks]);
      } catch {
        setSaveError(true);
      } finally {
        setLoading(false);
        if (!isCompleting) fetchProjectChecks();
      }
    },
    [instance, account, projectChecks, selectedPeriod, fetchProjectChecks],
  );

  const updateCheck = useCallback((id: number, value: boolean | string, type?: "checked") => {
    setProjectChecks((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              ...(type === "checked"
                ? { checkedByManager: value as boolean }
                : { managerComment: value as string }),
            }
          : p,
      ),
    );
  }, []);

  const confirmAll = useCallback(() => {
    setProjectChecks((prev) => prev.map((c) => ({ ...c, checkedByManager: true })));
  }, []);

  useEffect(() => {
    setDirty(hasChanges);
    return () => setDirty(false);
  }, [hasChanges, setDirty]);

  return {
    projectChecks,
    hasChanges,
    fetchProjectChecks,
    saveProjectChecks,
    updateCheck,
    confirmAll,
    loading,
    fetchError,
    saveError,
    allChecked,
    hasCompletedCheck,
  };
}
